/**
 * Team API — account membership, role catalog, and invitations (task 636).
 *
 * Everything here is a **read-only projection of AMS**, edited through AMS's own
 * flow: membership, the role catalog, and (task 634) account-scoped invitations
 * all live in AMS and reach the browser through innovate-service proxies. The
 * app stores nothing new — the Team page renders what these endpoints return.
 *
 * The account is ALWAYS derived server-side from the caller's token scope; the
 * client never sends an account id. That mirrors the profile proxy
 * (`/api/me/profile`) and the usage proxy (`/api/accounts/usage`).
 *
 * ─── Backend status (2026-08-20) ────────────────────────────────────────────
 * As of this page's build, NONE of these innovate-service proxies exist yet:
 *   - GET    /api/account/members            (members-list proxy — task 636 step 1)
 *   - PATCH  /api/account/members/:userId    (role change — owner/admin)
 *   - GET    /api/account/roles              (role-definition catalog)
 *   - GET    /api/account/invitations        (pending invites — task 634)
 *   - POST   /api/account/invitations        (create invite — task 634)
 *   - POST   /api/account/invitations/:id/resend
 *   - DELETE /api/account/invitations/:id    (revoke — task 634)
 * They are written here against their *intended* contract so the page is ready
 * the moment the proxies land. Until then every call 404s and the page renders
 * its "team management isn't available yet" state rather than crashing. The
 * invitation proxies are gated backend-side behind `innovate.be.account.invitations`.
 *
 * This module reuses the shared axios instance (default export of
 * `services/api`) so it inherits the bearer-token injection and 401-refresh
 * interceptors — it does NOT create a second client.
 */

import api from './api';
import type { SuccessResponse } from '../types';

/** The AMS account roles. `owner` is never assignable through this page. */
export type AccountRole = 'owner' | 'admin' | 'editor' | 'viewer';

/** An active member of the account, as AMS records it. */
export interface TeamMember {
  /** AMS user UUID — compare against `AuthUser.userId` to find "you". */
  userId: string;
  displayName: string;
  email: string;
  /** owner | admin | editor | viewer (kept as string — the catalog is authoritative). */
  role: string;
  /** ISO-8601 timestamp the membership began. */
  joinedAt: string;
  /** AMS membership status, e.g. `active`. */
  status: string;
}

/** A pending account invitation (task 634). */
export interface TeamInvite {
  id: string;
  email: string;
  role: string;
  /** ISO-8601 timestamp the invite was sent. */
  invitedAt: string;
  /** ISO-8601 timestamp the invite lapses (AMS default: 14 days). */
  expiresAt: string;
  /** Always `pending` for rows this page shows. */
  status: string;
}

/**
 * One role's catalog entry. The `summary` is authored in AMS — the Roles card
 * and the invite modal's role hint render THIS, never hardcoded capability copy.
 */
export interface RoleDefinition {
  role: string;
  /** Optional display label; falls back to the role key. */
  label?: string;
  summary: string;
  /** False for `owner` (not assignable via invite / role change). */
  assignable?: boolean;
}

/** Payload for changing a member's role. `owner` is rejected server-side. */
export interface MemberRoleUpdate {
  role: AccountRole;
}

/** Payload for creating an invitation. */
export interface CreateInviteInput {
  email: string;
  role: AccountRole;
}

export const teamApi = {
  /** GET /api/account/members — every member of the caller's account (any role may read). */
  listMembers: () =>
    api.get<SuccessResponse<TeamMember[]>>('/account/members'),

  /**
   * PATCH /api/account/members/:userId — change a member's role (owner/admin).
   * The server rejects targeting the owner row or setting `owner`.
   */
  updateMemberRole: (userId: string, update: MemberRoleUpdate) =>
    api.patch<SuccessResponse<TeamMember>>(`/account/members/${userId}`, update),

  /** GET /api/account/roles — the AMS role-definition catalog. */
  listRoles: () =>
    api.get<SuccessResponse<RoleDefinition[]>>('/account/roles'),

  /** GET /api/account/invitations — pending invites for the account (task 634). */
  listInvitations: () =>
    api.get<SuccessResponse<TeamInvite[]>>('/account/invitations'),

  /**
   * POST /api/account/invitations — invite an email at a role (owner/admin).
   * A 409 signals the email is already a member or already invited.
   */
  createInvitation: (input: CreateInviteInput) =>
    api.post<SuccessResponse<TeamInvite>>('/account/invitations', input),

  /** POST /api/account/invitations/:id/resend — re-email and refresh expiry. */
  resendInvitation: (id: string) =>
    api.post<SuccessResponse<TeamInvite>>(`/account/invitations/${id}/resend`),

  /** DELETE /api/account/invitations/:id — revoke a pending invite. */
  revokeInvitation: (id: string) =>
    api.delete<SuccessResponse<{ id: string }>>(`/account/invitations/${id}`),
};
